import React from 'react';
import { Box, Button, Card, CardContent, CardMedia, Chip, Divider, Typography, alpha, useTheme } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';

interface OfferDecisionMessageCardProps {
  productTitle: string;
  productPrice: number;
  productImageUrl?: string;
  offerPrice: number;
  offerText: string;
  status: 'accepted' | 'rejected';
  isOwnMessage: boolean;
  showPayNow?: boolean;
  onPayNow?: () => void;
  payNowDisabled?: boolean;
}

const currency = (n: number) => `$${n.toFixed(2)}`;

const OfferDecisionMessageCard: React.FC<OfferDecisionMessageCardProps> = ({
  productTitle,
  productPrice,
  productImageUrl,
  offerPrice,
  offerText,
  status,
  isOwnMessage,
  showPayNow = false,
  onPayNow,
  payNowDisabled = false,
}) => {
  const theme = useTheme();
  const isAccepted = status === 'accepted';

  const title = isAccepted ? 'Offer accepted' : 'Offer rejected';
  const subtitle = isAccepted
    ? (isOwnMessage ? 'You accepted this offer' : 'Your offer was accepted')
    : (isOwnMessage ? 'You rejected this offer' : 'Your offer was rejected');

  const icon = isAccepted
    ? <CheckCircleIcon sx={{ fontSize: 22, color: 'success.dark' }} />
    : <CancelIcon sx={{ fontSize: 22, color: 'error.dark' }} />;

  const chipColor = isAccepted ? 'success' : 'error';
  const borderColor = isAccepted ? 'success.200' : 'error.200';

  return (
    <Card
      sx={{
        display: 'flex',
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: isAccepted
          ? '0 10px 28px rgba(46, 125, 50, 0.18)'
          : '0 10px 28px rgba(211, 47, 47, 0.18)',
        border: '1px solid',
        borderColor,
        background: isAccepted
          ? 'linear-gradient(135deg, rgba(232, 245, 233, 1) 0%, rgba(255,255,255,1) 55%)'
          : 'linear-gradient(135deg, rgba(255, 235, 238, 1) 0%, rgba(255,255,255,1) 55%)',
      }}
    >
      {productImageUrl && (
        <CardMedia
          component="img"
          image={productImageUrl}
          alt={productTitle}
          sx={{ width: { xs: 92, sm: 132 }, objectFit: 'cover' }}
        />
      )}
      <Box sx={{ flex: 1 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              size="small"
              color={chipColor}
              icon={icon}
              label={title}
              sx={{ fontWeight: 800 }}
            />
            <Typography variant="subtitle2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            {productTitle}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Chip
              label={`Offer: ${currency(offerPrice)}`}
              sx={{
                bgcolor: isAccepted ? 'success.light' : 'grey.200',
                color: isAccepted ? 'success.dark' : 'text.primary',
                fontWeight: 800,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Listed price: {currency(productPrice)}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {offerText}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="body2" color={isAccepted ? 'success.main' : 'error.main'} sx={{ fontWeight: 700 }}>
            {isAccepted ? 'This offer is now accepted.' : 'This offer was not accepted.'}
          </Typography>

          {isAccepted && showPayNow && (
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: isOwnMessage ? 'flex-end' : 'flex-start' }}>
              <Button
                variant="contained"
                onClick={onPayNow}
                disabled={!onPayNow || payNowDisabled}
                sx={{
                  borderRadius: 999,
                  px: 2.5,
                  py: 1,
                  fontWeight: 800,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: `0 10px 26px ${alpha(theme.palette.primary.main, 0.25)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                    boxShadow: `0 14px 34px ${alpha(theme.palette.primary.main, 0.32)}`,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    boxShadow: 'none',
                    transform: 'none',
                  },
                }}
              >
                Pay Now
              </Button>
            </Box>
          )}
        </CardContent>
      </Box>
    </Card>
  );
};

export default OfferDecisionMessageCard;
